import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from "react";
import type { SessionRecord, SessionExercise } from "@/hooks/useSessions";
import { preserveState, restoreState, clearPreserved, PRESERVE_KEYS } from "@/lib/statePreserver";

export interface StepPerformance {
  reps?: string;
  weight?: string;
  time?: string;
  distance?: string;
  sets?: string;
  rpe?: number;
  notes?: string;
}

export interface StepData {
  exerciseIndex: number;
  exerciseName: string;
  done: boolean;
  performances: StepPerformance;
}

/** Optional adaptive signals passed at session start time */
export interface AdaptiveSignals {
  fatigueScore?: number | null;
  sleepLast?: number | null;
  painCount?: number;
}

interface ActiveSessionState {
  session: SessionRecord | null;
  logId: string | null;
  startedAt: Date | null;
  currentStep: number;
  steps: StepData[];
  isOpen: boolean;
  adaptiveSignals?: AdaptiveSignals;
}

interface ActiveSessionContextValue extends ActiveSessionState {
  startSession: (session: SessionRecord, signals?: AdaptiveSignals) => void;
  closeOverlay: () => void;
  openOverlay: () => void;
  setCurrentStep: (step: number) => void;
  toggleStepDone: (index: number) => void;
  updateStepPerformance: (index: number, perf: Partial<StepPerformance>) => void;
  setLogId: (id: string) => void;
  endSession: () => void;
}

const ActiveSessionContext = createContext<ActiveSessionContextValue | null>(null);

export function ActiveSessionProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<ActiveSessionState>(() => {
    // Restore from localStorage if available
    const restored = restoreState<ActiveSessionState>(PRESERVE_KEYS.ACTIVE_SESSION);
    if (restored?.session) {
      // Backfill exerciseName for steps saved before the tracking upgrade
      const exercises = restored.session.exercises || [];
      const patchedSteps = (restored.steps || []).map((step, i) => ({
        ...step,
        exerciseName: step.exerciseName || exercises[step.exerciseIndex]?.name || exercises[i]?.name || `Exercice ${i + 1}`,
        performances: {
          ...step.performances,
        },
      }));
      return {
        ...restored,
        steps: patchedSteps,
        startedAt: restored.startedAt ? new Date(restored.startedAt) : null,
        isOpen: false, // Don't auto-open overlay on restore
      };
    }
    return {
      session: null,
      logId: null,
      startedAt: null,
      currentStep: 0,
      steps: [],
      isOpen: false,
    };
  });

  // Auto-persist active session state to localStorage
  useEffect(() => {
    if (state.session) {
      preserveState(PRESERVE_KEYS.ACTIVE_SESSION, state);
    } else {
      clearPreserved(PRESERVE_KEYS.ACTIVE_SESSION);
    }
  }, [state]);

  // Show restore toast if session was recovered after hard reload
  useEffect(() => {
    if (state.session && !state.isOpen) {
      const wasReload = sessionStorage.getItem("perform_was_hard_reload");
      if (wasReload) {
        import("sonner").then(({ toast }) => {
          toast.info("🔄 Ta séance en cours a été préservée. Tu peux la reprendre.", {
            duration: 6000,
            action: {
              label: "Reprendre",
              onClick: () => setState((s) => ({ ...s, isOpen: true })),
            },
          });
        });
        sessionStorage.removeItem("perform_was_hard_reload");
      }
    }
  }, []); // Only on mount

  const startSession = useCallback((session: SessionRecord, signals?: AdaptiveSignals) => {
    const steps: StepData[] = (session.exercises || []).map((ex, i) => ({
      exerciseIndex: i,
      exerciseName: ex.name || `Exercice ${i + 1}`,
      done: false,
      performances: {},
    }));
    setState({
      session,
      logId: null,
      startedAt: new Date(),
      currentStep: 0,
      steps,
      isOpen: true,
      adaptiveSignals: signals,
    });
  }, []);

  const closeOverlay = useCallback(() => setState((s) => ({ ...s, isOpen: false })), []);
  const openOverlay = useCallback(() => setState((s) => ({ ...s, isOpen: true })), []);

  const setCurrentStep = useCallback((step: number) => {
    setState((s) => ({ ...s, currentStep: step }));
  }, []);

  const toggleStepDone = useCallback((index: number) => {
    setState((s) => {
      const steps = [...s.steps];
      steps[index] = { ...steps[index], done: !steps[index].done };
      return { ...s, steps };
    });
  }, []);

  const updateStepPerformance = useCallback((index: number, perf: Partial<StepPerformance>) => {
    setState((s) => {
      const steps = [...s.steps];
      steps[index] = {
        ...steps[index],
        performances: { ...steps[index].performances, ...perf },
      };
      return { ...s, steps };
    });
  }, []);

  const setLogId = useCallback((id: string) => {
    setState((s) => ({ ...s, logId: id }));
  }, []);

  const endSession = useCallback(() => {
    setState({
      session: null,
      logId: null,
      startedAt: null,
      currentStep: 0,
      steps: [],
      isOpen: false,
      adaptiveSignals: undefined,
    });
  }, []);

  return (
    <ActiveSessionContext.Provider
      value={{
        ...state,
        startSession,
        closeOverlay,
        openOverlay,
        setCurrentStep,
        toggleStepDone,
        updateStepPerformance,
        setLogId,
        endSession,
      }}
    >
      {children}
    </ActiveSessionContext.Provider>
  );
}

export function useActiveSession() {
  const ctx = useContext(ActiveSessionContext);
  if (!ctx) throw new Error("useActiveSession must be used within ActiveSessionProvider");
  return ctx;
}
